import { Controller } from "@hotwired/stimulus"
import ExifReader from 'exifreader';
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["originalPhotoInput", "photoInput", "previewContainer", "latitude", "longitude", "form", "loadingIndicator", "errorIndicator", "loadingText", "placeholderBox", "photoCaptureContainer", "cameraButton", "exifData", "originalPhotoUuid"]

  initialize() {
    this.directUploadCompleted = false;
    this.resizedPhotoReady = false;
    this.originalPhotoUuid = null;
  }

  connect() {
    this.handleOrientationBinded = this.handleOrientation.bind(this)
    this.formTarget.addEventListener('ajax:error', this.handleSubmitError.bind(this))
    this.formTarget.addEventListener('turbo:submit-end', (event) => {
      if (!event.detail.success) {
        this.handleSubmitError(event)
      }
    })
    this.initGeolocation()
    this.setupDirectUploadListeners()
  }

  initGeolocation() {
    // First use the centralized position if available
    if (window.currentPosition) {
      this.updateLocationData(window.currentPosition);
    }

    // Set up a callback for position updates
    this.positionUpdateCallback = (position) => {
      this.updateLocationData(position);
    };
    window.onPositionUpdate(this.positionUpdateCallback);
  }

  createHiddenInput(name, value) {
    let input = this.formTarget.querySelector(`input[name="discovery[device_info][${name}]"]`)
    if (!input) {
      input = document.createElement('input')
      input.type = 'hidden'
      input.name = `discovery[device_info][${name}]`
      this.formTarget.appendChild(input)
    }
    input.value = value || ''
  }

  updateLocationData(position) {
    // Store the complete position object
    this.createHiddenInput('raw_position', JSON.stringify(position))

    // Also store individual fields for easy access
    this.createHiddenInput('latitude', position.coords.latitude)
    this.createHiddenInput('longitude', position.coords.longitude)
    this.createHiddenInput('accuracy', position.coords.accuracy)
    this.createHiddenInput('accuracy_type', position.coords.accuracyType)
    this.createHiddenInput('altitude', position.coords.altitude)
    this.createHiddenInput('altitude_accuracy', position.coords.altitudeAccuracy)
    this.createHiddenInput('heading', position.coords.heading)
  }

  initDeviceOrientation() {
    // this information is useful and it seems like it's ok now even on Lis' phone
    // so let's keep this commented out for now until it becomes a annoying again.
    // if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) {
    //   console.log("Skipped Device orientation on iOS devices");
    //   return Promise.resolve(); // Skip on iOS devices
    // }
    console.log("Checking device orientation support...")
    return new Promise((resolve, reject) => {
      if (window.DeviceOrientationEvent) {
        console.log("DeviceOrientationEvent is supported")
        // Request permission for iOS 13+ devices
        if (typeof DeviceOrientationEvent.requestPermission === 'function') {
          console.log("Requesting iOS permission for device orientation...")
          DeviceOrientationEvent.requestPermission()
            .then(permissionState => {
              console.log("iOS permission state:", permissionState)
              if (permissionState === 'granted') {
                console.log("Adding device orientation listener")
                // Remove any existing device orientation event listener
                window.removeEventListener('deviceorientation', this.handleOrientationBinded)
                window.addEventListener('deviceorientation', this.handleOrientationBinded)
                resolve()
              } else {
                console.log("iOS permission denied for device orientation")
                reject(new Error("Permission denied"))
              }
            })
            .catch(error => {
              console.error("Error requesting iOS permission:", error)
              reject(error)
            })
        } else {
          // For non-iOS devices, just add the listener
          console.log("Adding device orientation listener (non-iOS device)")
          window.removeEventListener('deviceorientation', this.handleOrientationBinded)
          window.addEventListener('deviceorientation', this.handleOrientationBinded)
          resolve()
        }
      } else {
        console.log("DeviceOrientationEvent is NOT supported on this device")
        reject(new Error("Not supported"))
      }
    })
  }

  handleOrientation(event) {
    console.log("Device orientation event received:", event)
    // Also store individual fields for easy access
    this.createHiddenInput('orientation_alpha', event.alpha)
    this.createHiddenInput('orientation_beta', event.beta)
    this.createHiddenInput('orientation_gamma', event.gamma)
    this.createHiddenInput('orientation_absolute', event.absolute)
  }

  openCamera(event) {
    // If the event was triggered by clicking directly on the input, don't
    // re-trigger the click as it would cause an infinite loop
    if (event && (event.target === this.originalPhotoInputTarget || event.target === this.photoInputTarget)) {
      return
    }

    // Request orientation permission before opening camera
    this.initDeviceOrientation().then(() => {
      // Set capture attribute to camera to open camera directly
      this.originalPhotoInputTarget.click()
    }).catch(error => {
      console.error("Error initializing device orientation:", error)
      // Still open camera even if orientation fails
      this.originalPhotoInputTarget.click()
    })
  }

  setupDirectUploadListeners() {
    if (!this.hasOriginalPhotoInputTarget) {
      return;
    }

    const fileInput = this.originalPhotoInputTarget;

    console.log('Setting up direct upload listeners for original photo input');

    // Listen for direct upload events
    fileInput.addEventListener('direct-upload:initialize', (event) => {
      console.log('Direct upload initialized for original photo');
    });

    fileInput.addEventListener('direct-upload:start', (event) => {
      console.log('Direct upload started for original photo');
    });

    fileInput.addEventListener('direct-upload:progress', (event) => {
      console.log(`Direct upload progress: ${event.detail.progress}%`);
    });

    fileInput.addEventListener('direct-upload:error', (event) => {
      console.error('Direct upload failed:', event.detail.error);
      this.handleSubmitError(event)
    });

    fileInput.addEventListener('direct-upload:end', (event) => {
      console.log('Direct upload completed for original photo');
      console.log('Event detail:', event.detail);
      console.log('File input value after upload:', fileInput.value);

      this.directUploadCompleted = true;
    });
  }

  originalPhotoSelected() {
    this.cameraButtonTarget.classList.add('is-hidden');
    this.loadingIndicatorTarget.classList.remove('is-hidden');

    const fileInput = this.originalPhotoInputTarget

    if (fileInput.files.length > 0) {
      const file = fileInput.files[0];

      console.log('Photo selected:', file.name, 'Size:', file.size);

      // Add original photo to PhotoUploader queue
      this.addOriginalPhotoToQueue(file);

      // Get final location and orientation data before proceeding
      this.captureFinalData()

      // Show preview immediately for better UX
      this.previewPhoto(file);

      // Create resized version for immediate processing
      this.createResizedPhoto(file);
    }
  }

  addOriginalPhotoToQueue(file) {
    if (!window.photo_uploader) {
      console.error('PhotoUploader not available');
      return;
    }

    // Add to upload queue and get UUID
    this.originalPhotoUuid = window.photo_uploader.addToQueue(file, {
      onProgress: (progress, uploadInfo) => {
        console.log(`Original photo upload progress: ${progress}%`);
      },
      onComplete: (result, uploadInfo) => {
        console.log('Original photo upload completed:', result);
        // The original photo is now uploaded in the background
        // We don't need to wait for it to submit the form
      },
      onError: (error, uploadInfo) => {
        console.error('Original photo upload failed:', error);
        // We don't block form submission on original photo upload failure
      }
    });

    // Clear the original photo file input since we don't want to upload it through the form
    this.originalPhotoInputTarget.value = '';

    // Set the UUID in the form instead
    if (this.hasOriginalPhotoUuidTarget) {
      this.originalPhotoUuidTarget.value = this.originalPhotoUuid;
    }

    console.log('Original photo added to queue with UUID:', this.originalPhotoUuid);
    console.log('Original photo file input cleared to prevent form upload');
  }

  async createResizedPhoto(file) {
    try {
      // Read EXIF data from original file first
      const exifData = await this.readExif(file);
      if (this.hasExifDataTarget && exifData) {
        this.exifDataTarget.value = JSON.stringify(exifData);
      }

      // Resize the image
      const resizedBlob = await this.resizeImage(file, 768);

      // Create the resized file
      const resizedFile = new File([resizedBlob], file.name, {
        type: 'image/jpeg',
        lastModified: exifData?.DateTimeOriginal?.description ?
          new Date(exifData.DateTimeOriginal.description).getTime() :
          new Date().getTime()
      });

      // Set the resized file to the photo input (for Rails upload)
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(resizedFile);
      this.photoInputTarget.files = dataTransfer.files;

      this.resizedPhotoReady = true;
      console.log('Resized photo ready');

      this.formTarget.requestSubmit();
    } catch (err) {
      console.error("Error processing image:", err);
      this.handleSubmitError({ detail: { error: err } });
    }
  }

  async readExif(imageFile) {
    try {
      const arrayBuffer = await imageFile.arrayBuffer();
      const exifData = ExifReader.load(arrayBuffer);
      // Convert ExifReader data to a more usable format
      for (const tag in exifData) {
        if (exifData[tag].description) {
          const desc = exifData[tag].description;
          if (typeof desc === 'string' && desc.length > 1000) {
            delete exifData[tag]
          } else {
            exifData[tag] = desc
          }
        } else {
          delete exifData[tag]
        }
      }
      return exifData;
    } catch (error) {
      console.warn("Failed to read EXIF data:", error);
      return null;
    }
  }

  photoSelected() {
    // This method is now deprecated in favor of originalPhotoSelected
    // but keeping it for backwards compatibility or fallback
    console.warn("photoSelected() is deprecated, use originalPhotoSelected() instead");
    this.originalPhotoSelected();
  }

  async captureFinalData() {
    // Get the most recent position update
    if (window.currentPosition) {
      // Use the latest cached position
      this.updateLocationData(window.currentPosition);
    }

    // Also request a fresh position (high accuracy)
    try {
      const position = await window.getCurrentPosition(true);
      this.updateLocationData(position);
    } catch (error) {
      console.log("Error getting final location: ", error);
    }
  }

  previewPhoto(file) {
    if (!this.hasPreviewContainerTarget) {
      return
    }

    const reader = new FileReader()

    reader.onload = (e) => {
      // Hide the loading indicator while showing the preview
      this.loadingIndicatorTarget.classList.add('is-hidden')

      // Show the preview container
      const preview = document.createElement('img')
      preview.src = e.target.result
      preview.style.width = '100%'
      preview.style.height = '100%'
      preview.style.objectFit = 'cover'
      preview.style.borderRadius = '8px'

      // Remove any existing preview
      const previewContainer = this.previewContainerTarget
      previewContainer.innerHTML = ''

      // Add new preview
      previewContainer.appendChild(preview)
    }

    reader.readAsDataURL(file)
  }

  selectRating(event) {
    // Remove selected class from all rating buttons
    this.element.querySelectorAll('.rating-input').forEach(input => {
      input.closest('label').classList.remove('is-info', 'is-selected')
    })

    // Add selected class to clicked button
    event.currentTarget.classList.add('is-info', 'is-selected')
  }

  disconnect() {
    // Clean up position update callback
    if (this.positionUpdateCallback) {
      window.offPositionUpdate(this.positionUpdateCallback);
      this.positionUpdateCallback = null;
    }
  }

  /**
   * Resizes an image so the shorter dimension is constrained to a specified size while maintaining aspect ratio
   * @param {File} file - The image file to resize
   * @param {Number} maxSizeOnShorterDimension - The maximum size for the shorter dimension in pixels
   * @returns {Promise<Blob>} - A promise that resolves with the resized image blob
   */
  resizeImage(file, maxSizeOnShorterDimension) {
    return new Promise((resolve, reject) => {
      // Create image and canvas elements
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Create a blob URL to load the image
      const url = URL.createObjectURL(file);

      img.onload = () => {
        // Release the object URL
        URL.revokeObjectURL(url);

        // Determine dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;

        // Constrain the shorter dimension to maxSizeOnShorterDimension
        const shorterDimension = Math.min(width, height);
        if (shorterDimension > maxSizeOnShorterDimension) {
          const scaleFactor = maxSizeOnShorterDimension / shorterDimension;
          width = Math.round(width * scaleFactor);
          height = Math.round(height * scaleFactor);
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw resized image on canvas
        ctx.drawImage(img, 0, 0, width, height);

        // Get the resized image as a blob with 0.9 quality jpeg
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas to Blob conversion failed'));
            }
          },
          'image/jpeg',
          0.85
        );
      };

      // Handle image loading errors
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Error loading image'));
      };

      // Begin loading the image
      img.src = url;
    });
  }

  // Handle form submission errors
  handleSubmitError(event) {
    console.error('Form submission failed:', event)

    this.loadingIndicatorTarget.classList.add('is-hidden')
    this.cameraButtonTarget.classList.add('is-hidden')
    this.errorIndicatorTarget.classList.remove('is-hidden')

    // After 3 seconds, hide error and show camera button again
    setTimeout(() => {
      this.errorIndicatorTarget.classList.add('is-hidden')
      this.cameraButtonTarget.classList.remove('is-hidden')
    }, 3000)
  }
}
